import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import { DataGridCardProps } from "designSystem/components/dataGrid/DataGrid";
import Grid from "designSystem/components/grid/Grid";
import SearchSingleSelect from "designSystem/components/select/SearchSingleSelect";
import TextField from "designSystem/components/textfield/TextField";
import NodeDataStore from "csi/datastore/NodeDataStore";
import { IDataStore } from "csi/typings";
import React, { FunctionComponent, useEffect, useState } from "react";
import { GridColumn } from "../grid/GridContainer";
import { ILocaleProvider, useLocale } from "designSystem/locale";
import PopperInContainer from "../popper/PopperInContainer";
import { onSearchComponentItemDataStore } from "./SearchSingleSelect";
import { ComponentItem } from "designSystem/types/component-item-types";

export interface DataGridSingleSelectProps<T> {
	/** card component */
	GridCard: FunctionComponent<DataGridCardProps<T>>;

	/** indicate whether or not disable the component so that user can't be able to open the dropdown
	 * Note: in contrast, "getDisableItem" is to make the item in the dropdown disabled
	 */
	disabled?: boolean;

	/** callback to determine whether or not should disable this item so that the user can't be able to select that item
	 * Note: in contrast, "disabled" is to make textbox disabled 
	*/
	getDisableItem?: (item: T) => boolean;

	/** function to render the text in the textbox */
	getDisplayText: (id: string, loc: ILocaleProvider) => string;

	/** the type of the item */
	itemType: string;

	/** callback to convert full store into filtered store by search text */
	onSearchStore?: (store: IDataStore<T> | NodeDataStore, searchText: string, loc: ILocaleProvider) => Promise<IDataStore<T> | NodeDataStore>;

	/** callback called when an item is selected or when blurred/escaped with no value */
	onSelectItem: (value?: string) => void;

	/** the store for the cards */
	store: IDataStore<T>;

	/** the selected value */
	value?: string;
}

const DataGridSingleSelect = <T extends ComponentItem>(props: DataGridSingleSelectProps<T>) => {
	const [anchorEl, setAnchorEl] = useState(null);
	const {
		GridCard,
		disabled,
		getDisplayText,
		getDisableItem,
		itemType,
		onSelectItem,
		onSearchStore,
		store,
		value,
	} = props;
	const loc = useLocale();

	const [id, setId] = useState<string>(value || "");
	const [text, setText] = useState<string>(getDisplayText(id, loc) || "");

	/** close the popup and proceed */
	const _onClickTextBox = (event) => {
		if (disabled) {
			return;
		}

		setAnchorEl(anchorEl ? null : event.currentTarget);
	};

	/** update the value of textbox to keep the value in sync */
	const _onSelectItem = (value?: string) => {
		anchorEl?.focus();
		setAnchorEl(null);

		if (value) {
			setId(value);
			setText(getDisplayText(value, loc));
		}

		onSelectItem?.(value);
	};

	useEffect(() => {
		setText(getDisplayText(value, loc));
		setId(value);
	}, [value]);

	return (
		<GridColumn>
			<Grid item>
				<TextField
					InputProps={{
						endAdornment: <ArrowDropDownIcon />,
						style: {
							paddingRight: 4,
						},
					}}
					disabled={disabled}
					fullWidth
					onClick={_onClickTextBox}
					placeholderRes="SelectSomething"
					placeholderResParams={[itemType]}
					style={{
						width: 200,
					}}
					value={text}
				/>

			</Grid>
			<Grid item>
				<PopperInContainer anchorEl={anchorEl}>
					<SearchSingleSelect<T>
						dataGridProps={{
							CardComponent: GridCard,
							store,
						}}
						getDisableItem={getDisableItem}
						inPopup
						listItemProps={{
							height: 56,
						}}
						maxHeight={300}
						onSearchStore={onSearchStore || onSearchComponentItemDataStore}
						onSelect={_onSelectItem}
						style={{
							width: 200,
						}}
						value={id}
					/>
				</PopperInContainer>
			</Grid>
		</GridColumn>
	);
};

export default DataGridSingleSelect;